// frontend/src/components/LegalDoc.js
//
// A legal text, rendered from the translation catalogue.
//
// Terms.js and PrivacyPolicy.js each hand-write their own <Section> per clause,
// which is fine for two documents that rarely change shape and terrible for a
// third and fourth: adding a clause meant editing a page as well as two locale
// files, and a clause added to only one language rendered as a raw key.
//
// Here the SHAPE comes from the catalogue. A section is s1, s2, … and its
// contents are whatever keys it has — `title`, then `p1`, `p2`, … as paragraphs
// and `l1`, `l2`, … as a list. Adding a clause is a locale edit and nothing
// else, and a clause that exists in one language and not the other is
// impossible to render half-way: the section is read from the ACTIVE language's
// tree, so a missing translation is a missing section rather than a key on
// screen — visible, and caught by the parity check.

import React, { useEffect } from 'react';
import { Link } from '../routing';
import { Sparkles } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import TraderIdentity from './TraderIdentity';
import { useCompany } from '../utils/useCompany';
import PageHero from './PageHero';
import theme from '../theme';

const LINK = { color: theme.gold };

/**
 * The section keys present under a namespace, in numeric order.
 *
 * Read out of i18next's own store rather than from a hardcoded list, and sorted
 * NUMERICALLY — a lexicographic sort puts s10 between s1 and s2, which reorders
 * the clauses of a contract, and nobody would notice until it mattered.
 */
function sectionKeys(i18n, namespace) {
  const bundle = i18n.getResourceBundle(i18n.language, 'translation') || {};
  const doc = bundle[namespace] || {};
  return Object.keys(doc)
    .filter((k) => /^s\d+$/.test(k))
    .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
}

const orderedSubKeys = (section, prefix) =>
  Object.keys(section || {})
    .filter((k) => new RegExp(`^${prefix}\\d+$`).test(k))
    .sort((a, b) => Number(a.slice(prefix.length)) - Number(b.slice(prefix.length)));

export default function LegalDoc({ namespace, values = {} }) {
  const { t, i18n } = useTranslation();
  const company = useCompany();

  // Inline markup used inside translated sentences. The email is a component
  // rather than a string so the address is never duplicated into a translation
  // and never translated — the same reasoning as Terms.js, where a hardcoded
  // address on a dead domain survived in the text for months.
  const components = {
    b: <strong />,
    i: <em />,
    br: <br />,
    mail: <a href={`mailto:${company.email}`} style={LINK}>{company.email}</a>,
  };

  const T = ({ k }) => <Trans i18nKey={k} values={values} components={components} />;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const bundle = i18n.getResourceBundle(i18n.language, 'translation') || {};
  const doc = bundle[namespace] || {};

  return (
    <div style={{ fontFamily: theme.font.body, background: theme.cream, minHeight: '100vh' }}>
      <nav style={{
        background: theme.espresso, padding: '20px 40px', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <Sparkles size={18} color={theme.gold} />
          <span style={{
            fontFamily: theme.font.display, fontSize: 24, fontWeight: 600,
            color: theme.white, letterSpacing: '0.05em',
          }}>
            GLAM<span style={{ color: theme.gold }}>GO</span>
          </span>
        </Link>
        <Link to="/" style={{
          color: theme.alpha.white(0.6), textDecoration: 'none',
          fontSize: 12, letterSpacing: '0.12em',
        }}>{t(`${namespace}.back`)}</Link>
      </nav>

      <PageHero
        eyebrow={t(`${namespace}.label`)}
        title={t(`${namespace}.title`)}
        subtitle={t(`${namespace}.lastUpdated`, {
          date: t(`${namespace}.updatedDate`),
          version: values.version || '—',
        })}
        height={280}
      />

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '64px 24px' }}>
        {doc.intro && (
          <p style={{
            color: theme.ink600, fontSize: 16.5, lineHeight: 1.85,
            margin: '0 0 48px', paddingBottom: 32, borderBottom: `1px solid ${theme.sand}`,
          }}>
            <T k={`${namespace}.intro`} />
          </p>
        )}

        {sectionKeys(i18n, namespace).map((key) => {
          const section = doc[key];
          const paragraphs = orderedSubKeys(section, 'p');
          const items = orderedSubKeys(section, 'l');
          // A section that opens with prose and then lists is the common shape;
          // one that opens straight into a list (the "what you may not do"
          // clauses) is the other. Rendering p1 before the list and the rest
          // after covers both without a per-document layout.
          const [lead, ...rest] = items.length ? paragraphs : [];

          return (
            <section key={key} style={{ marginBottom: 48 }}>
              <h2 style={{
                fontFamily: theme.font.display, fontSize: 26, color: theme.ink,
                fontWeight: 500, margin: '0 0 16px',
                borderBottom: `1px solid ${theme.sand}`, paddingBottom: 12,
              }}>
                {t(`${namespace}.${key}.title`)}
              </h2>

              <div style={{ color: theme.ink600, fontSize: 15, lineHeight: 1.8 }}>
                {items.length === 0
                  ? paragraphs.map((p) => <p key={p}><T k={`${namespace}.${key}.${p}`} /></p>)
                  : (
                    <>
                      {lead && <p><T k={`${namespace}.${key}.${lead}`} /></p>}
                      <ul style={{ paddingLeft: 22, margin: '0 0 16px' }}>
                        {items.map((l) => (
                          <li key={l} style={{ marginBottom: 10 }}>
                            <T k={`${namespace}.${key}.${l}`} />
                          </li>
                        ))}
                      </ul>
                      {rest.map((p) => <p key={p}><T k={`${namespace}.${key}.${p}`} /></p>)}
                    </>
                  )}
              </div>
            </section>
          );
        })}

        {/* Who the other party actually is. A contract that does not name its
            counterparty is not a contract, and this is the same block the
            consumer Terms carry. */}
        <TraderIdentity />
      </div>
    </div>
  );
}
